import { Form, Formik, FormikHelpers } from 'formik';
import React, { useContext, useState } from 'react';
import useSWR from 'swr';
import { OperatorConfigUpdateInput } from '../../../../../../../typings/OutpostDeployment.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../common/base/Card';
import Container from '../../../common/base/Container';
import Divider from '../../../common/base/Divider';
import Icon from '../../../common/base/Icon';
import Skeleton from '../../../common/base/Skeleton';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import CustomDomainStatus from '../../../common/CustomDomainStatus';
import { useDialog } from '../../../common/Dialog';
import SelectInput from '../../../common/Form/Fields/SelectInput';
import SwitchInput, { StyledSwitch } from '../../../common/Form/Fields/SwitchInput';
import TextInput from '../../../common/Form/Fields/TextInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { DashboardContext } from '../../DashboardScene/DashboardContext';
import { useOutpostConfigUpdate } from '../OutpostContext';
interface GeneralFormValues {
  PORTAL_ORGANIZATION_NAME: string;
  PORTAL_REFERER_URL: string;
  PORTAL_REFRESH_URL: string;
}
interface ThemeFormValues {
  PORTAL_FORCE_THEME: string;
  PORTAL_BRAND_COLOR: string;
  PORTAL_DISABLE_OUTPOST_BRANDING: boolean;
}
interface LogosFormValues {
  PORTAL_LOGO: string;
  PORTAL_LOGO_DARK: string;
  PORTAL_FAVICON_URL: string;
}
const PORTAL_CNAME = process.env.OUTPOST_PORTAL_URL!.split('//')[1];
const TeamUserPortal: React.FC = () => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    organization
  } = useContext(DashboardContext);
  const {
    config,
    updateConfig
  } = useOutpostConfigUpdate();
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const [deleting, setDeleting] = useState(false);
  const [show_domain_form, setShowDomainForm] = useState(false);
  const {
    data: custom_domain,
    mutate: mutate_domain,
    isValidating: is_validating,
    isLoading: is_loading
  } = useSWR(APIMethodKeys.outpost.getCustomDomain(), () => HookdeckAPI.outpost.getCustomDomain(), {
    refreshInterval(latest) {
      // Refresh while domain is pending
      if (latest?.ssl?.status === 'initializing') {
        return 5 * 1000;
      }
      return 0;
    }
  });
  const portal_enabled = !!custom_domain;
  const custom_domain_configured = !!custom_domain && custom_domain.status === 'active' && custom_domain.ssl?.status === 'active';

  // Initial values for each form section
  const general_initial_values: GeneralFormValues = {
    PORTAL_ORGANIZATION_NAME: config.PORTAL_ORGANIZATION_NAME || organization?.name || '',
    PORTAL_REFERER_URL: config.PORTAL_REFERER_URL ?? '',
    PORTAL_REFRESH_URL: config.PORTAL_REFRESH_URL ?? ''
  };
  const theme_initial_values: ThemeFormValues = {
    PORTAL_FORCE_THEME: config.PORTAL_FORCE_THEME ?? '',
    PORTAL_BRAND_COLOR: config.PORTAL_BRAND_COLOR ?? '',
    PORTAL_DISABLE_OUTPOST_BRANDING: config.PORTAL_DISABLE_OUTPOST_BRANDING === 'true'
  };
  const logos_initial_values: LogosFormValues = {
    PORTAL_LOGO: config.PORTAL_LOGO ?? '',
    PORTAL_LOGO_DARK: config.PORTAL_LOGO_DARK ?? '',
    PORTAL_FAVICON_URL: config.PORTAL_FAVICON_URL ?? ''
  };
  const validateUrl = (value: string): string | undefined => {
    if (value && value.trim()) {
      try {
        new URL(value);
      } catch {
        return 'Must be a valid URL';
      }
    }
    return undefined;
  };
  const validateGeneral = (values: GeneralFormValues) => {
    const errors: Partial<Record<keyof GeneralFormValues, string>> = {};
    const referer_error = validateUrl(values.PORTAL_REFERER_URL);
    if (referer_error) {
      errors.PORTAL_REFERER_URL = referer_error;
    }
    const refresh_error = validateUrl(values.PORTAL_REFRESH_URL);
    if (refresh_error) {
      errors.PORTAL_REFRESH_URL = refresh_error;
    }
    return errors;
  };
  const validateTheme = (values_0: ThemeFormValues) => {
    const errors_0: Partial<Record<keyof ThemeFormValues, string>> = {};
    if (values_0.PORTAL_BRAND_COLOR && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(values_0.PORTAL_BRAND_COLOR)) {
      errors_0.PORTAL_BRAND_COLOR = 'Must be a valid hex color (e.g., #6122E7)';
    }
    return errors_0;
  };
  const validateLogos = (values_1: LogosFormValues) => {
    const errors_1: Partial<Record<keyof LogosFormValues, string>> = {};
    const url_fields: Array<keyof LogosFormValues> = ['PORTAL_LOGO', 'PORTAL_LOGO_DARK', 'PORTAL_FAVICON_URL'];
    url_fields.forEach(field => {
      const error = validateUrl(values_1[field]);
      if (error) {
        errors_1[field] = error;
      }
    });
    return errors_1;
  };
  const handleGeneralSubmit = async (values_2: GeneralFormValues, {
    resetForm
  }: FormikHelpers<GeneralFormValues>) => {
    const updated_config: OperatorConfigUpdateInput = {
      PORTAL_ORGANIZATION_NAME: values_2.PORTAL_ORGANIZATION_NAME || organization?.name || '',
      PORTAL_REFERER_URL: values_2.PORTAL_REFERER_URL || null,
      PORTAL_REFRESH_URL: values_2.PORTAL_REFRESH_URL || null
    };
    const result = await updateConfig(updated_config, {
      success: 'General settings updated',
      failure: 'Failed to update general settings'
    });
    if (!result) return;
    resetForm({
      values: {
        PORTAL_ORGANIZATION_NAME: result.PORTAL_ORGANIZATION_NAME ?? '',
        PORTAL_REFERER_URL: result.PORTAL_REFERER_URL ?? '',
        PORTAL_REFRESH_URL: result.PORTAL_REFRESH_URL ?? ''
      }
    });
  };
  const handleThemeSubmit = async (values_3: ThemeFormValues, {
    resetForm: resetForm_0
  }: FormikHelpers<ThemeFormValues>) => {
    const updated_config_0: OperatorConfigUpdateInput = {
      // Branding enabled is the binary's default — clear the override instead of writing 'false'.
      PORTAL_DISABLE_OUTPOST_BRANDING: values_3.PORTAL_DISABLE_OUTPOST_BRANDING ? 'true' : null,
      // '' means "Auto" — clear the override instead of writing an empty string.
      PORTAL_FORCE_THEME: values_3.PORTAL_FORCE_THEME || null,
      PORTAL_BRAND_COLOR: values_3.PORTAL_BRAND_COLOR || null
    };
    const result_0 = await updateConfig(updated_config_0, {
      success: 'Theme & branding updated',
      failure: 'Failed to update theme & branding'
    });
    if (!result_0) return;
    resetForm_0({
      values: {
        PORTAL_FORCE_THEME: result_0.PORTAL_FORCE_THEME ?? '',
        PORTAL_BRAND_COLOR: result_0.PORTAL_BRAND_COLOR ?? '',
        PORTAL_DISABLE_OUTPOST_BRANDING: result_0.PORTAL_DISABLE_OUTPOST_BRANDING === 'true'
      }
    });
  };
  const handleLogosSubmit = async (values_4: LogosFormValues, {
    resetForm: resetForm_1
  }: FormikHelpers<LogosFormValues>) => {
    const updated_config_1: OperatorConfigUpdateInput = {
      PORTAL_LOGO: values_4.PORTAL_LOGO || null,
      PORTAL_LOGO_DARK: values_4.PORTAL_LOGO_DARK || null,
      PORTAL_FAVICON_URL: values_4.PORTAL_FAVICON_URL || null
    };
    const result_1 = await updateConfig(updated_config_1, {
      success: 'Portal logos updated',
      failure: 'Failed to update portal logos'
    });
    if (!result_1) return;
    resetForm_1({
      values: {
        PORTAL_LOGO: result_1.PORTAL_LOGO ?? '',
        PORTAL_LOGO_DARK: result_1.PORTAL_LOGO_DARK ?? '',
        PORTAL_FAVICON_URL: result_1.PORTAL_FAVICON_URL ?? ''
      }
    });
  };
  const handleDisablePortal = () => {
    if (!custom_domain) return;
    showDialog(async () => {
      setDeleting(true);
      try {
        await HookdeckAPI.outpost.deleteCustomDomain();
        mutate_domain(null);
        addToast('success', 'Portal disabled');
      } catch {
        addToast('error', 'Failed to disable portal');
      } finally {
        setDeleting(false);
      }
    }, undefined, {
      title: 'Disable Portal',
      submit_label: 'Disable',
      cancel_label: 'Cancel',
      danger: true,
      message: 'Disabling the portal will remove the custom domain configuration. Users will no longer be able to access the portal.'
    });
  };
  const onDomainDeleted = () => {
    showDialog(async () => {
      setDeleting(true);
      try {
        await HookdeckAPI.outpost.deleteCustomDomain();
        mutate_domain(null);
        setShowDomainForm(true); // Keep portal enabled to allow adding a new domain
        addToast('success', 'Domain removed');
      } catch {
        addToast('error', 'The domain could not be removed, try again later.');
      } finally {
        setDeleting(false);
      }
    }, undefined, {
      submit_label: 'Remove domain',
      cancel_label: 'Cancel',
      submit_icon: 'delete',
      danger: true,
      message: 'Are you sure you want to remove the domain? The portal will stop working.'
    });
  };
  const onDomainRefresh = () => {
    mutate_domain();
  };
  return <Container medium left m={{
    b: 10
  }}>
      <Text bold size="l" as="h2" m={0}>
        User Portal
      </Text>
      <Text muted m={{
      b: 6
    }}>
        Allow your users to access the Outpost Portal via a custom domain.
      </Text>

      {/* Portal Enable/Disable Toggle */}
      <StyledCard m={{
      b: 6
    }}>
        <StyledCardSection flex={{
        justify: 'space-between',
        align: 'center'
      }} p={{
        x: 4,
        y: 3
      }}>
          <Div flex={{
          align: 'center',
          gap: 3
        }}>
            <StyledSwitch as="label" has_label={false} disabled={deleting}>
              <input checked={portal_enabled || show_domain_form} disabled={deleting} type="checkbox" onChange={e => {
              if (e.target.checked) {
                setShowDomainForm(true);
              } else {
                if (portal_enabled) {
                  handleDisablePortal();
                } else {
                  setShowDomainForm(false);
                }
              }
            }} />
            </StyledSwitch>
            <Div flex={{
            align: 'center',
            gap: 1
          }}>
              <Text>Enable user portal</Text>
              <Tooltip tooltip="Enable the portal to allow your users to access the Outpost Portal via a custom domain. The portal allows your end users to create destinations, view event deliveries and trigger retries.">
                <Icon muted icon="info" />
              </Tooltip>
            </Div>
          </Div>
          {(portal_enabled || show_domain_form) && !custom_domain_configured && <Badge warning icon="info" subtle>
              Pending Setup
            </Badge>}
        </StyledCardSection>
      </StyledCard>

      {/* Custom Domain Section (shown when portal is enabled or enabling) */}
      {(portal_enabled || show_domain_form) && <>
          <Text bold size="l" as="h2" m={{
        b: 0
      }}>
            Portal Domain{' '}
            <Text danger as="span" size="s">
              *
            </Text>
          </Text>
          <Text muted m={{
        b: 4
      }}>
            Configure the URL your users will be redirected to when accessing the portal.
          </Text>

          {is_loading && <Skeleton w={100} h={{
        px: 38
      }} />}

          {!is_loading && !custom_domain && <Formik initialValues={{
        hostname: ''
      }} validate={({
        hostname
      }) => {
        if (!hostname || hostname.length === 0) {
          return {
            hostname: 'Required'
          };
        }
        if (!/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9-]{2,61}\.[a-z]{2,}$/i.test(hostname)) {
          return {
            hostname: 'Invalid hostname. Hostname must contain a subdomain.'
          };
        }
        return {};
      }} onSubmit={async (values_5, {
        setFieldError
      }) => {
        try {
          const domain = await HookdeckAPI.outpost.addCustomDomain(values_5.hostname);
          mutate_domain(domain);
          addToast('success', 'Domain added. Complete the DNS configuration below.');
          setShowDomainForm(false);
        } catch (e_0: any) {
          if (e_0.response?.code === 'CUSTOM_DOMAIN_INVALID') {
            setFieldError('hostname', 'The domain entered is not a valid hostname.');
          } else if (e_0.response?.code === 'CUSTOM_DOMAIN_ALREADY_EXISTS') {
            setFieldError('hostname', 'The domain entered is already in use.');
          } else {
            setFieldError('hostname', 'Failed to add custom domain.');
          }
        }
      }}>
              {props => <Form>
                  <TextInput w={100} m={0} name="hostname" placeholder="portal.example.com" />
                  <Button m={{
            t: 6
          }} primary submit disabled={props.isSubmitting || !props.values.hostname} icon={props.isSubmitting ? 'loading' : undefined}>
                    Configure
                  </Button>
                </Form>}
            </Formik>}

          {!is_loading && custom_domain && <CustomDomainStatus custom_domain={custom_domain} cname_target={PORTAL_CNAME} is_validating={is_validating} is_deleting={deleting} onRefresh={onDomainRefresh} onDelete={onDomainDeleted} />}

          <Divider m={{
        y: 6
      }} />
        </>}

      {/* Portal Settings Forms (shown when portal is enabled) */}
      {(portal_enabled || show_domain_form) && <>
          <Text bold size="l" as="h2" m={0}>
            Portal Configuration
          </Text>
          <Text muted m={{
        b: 6
      }}>
            Configure the appearance and behavior of the Outpost Portal for your users.
          </Text>

          {/* General Settings Form */}
          <Formik enableReinitialize initialValues={general_initial_values} validate={validateGeneral} validateOnChange={false} validateOnBlur={true} onSubmit={handleGeneralSubmit}>
            {props_0 => <Form>
                <TextInput name="PORTAL_ORGANIZATION_NAME" label="Organization Name" placeholder="Acme Inc." m={{
            b: 4
          }} />

                <TextInput name="PORTAL_REFERER_URL" label="Referer URL" help="The URL to which the user is redirected when exiting the portal or when their authentication has expired. Generally this will be your own user dashboard URL." placeholder="https://admin.example.com" m={{
            b: 4
          }} />

                <TextInput name="PORTAL_REFRESH_URL" label="Refresh URL" help="URL for re-authentication when the portal JWT expires" placeholder="https://admin.example.com/auth/refresh" m={{
            b: 0
          }} />

                <Button.Permission m={{
            t: 6
          }} primary submit disabled={!props_0.isValid || props_0.isSubmitting || !props_0.dirty} icon={props_0.isSubmitting ? 'loading' : 'save'}>
                  Save
                </Button.Permission>
              </Form>}
          </Formik>

          <Divider m={{
        y: 6
      }} />

          {/* Theme & Branding Form */}
          <Text bold size="l" as="h3" m={{
        t: 0,
        b: 3
      }}>
            Portal Theme & Branding
          </Text>

          <Formik enableReinitialize initialValues={theme_initial_values} validate={validateTheme} validateOnChange={false} validateOnBlur={true} onSubmit={handleThemeSubmit}>
            {props_1 => <Form>
                <SelectInput name="PORTAL_FORCE_THEME" label="Force Theme" help="Force a specific theme for the portal" placeholder="Auto (user preference)" options={[{
            label: 'Auto (user preference)',
            value: ''
          }, {
            label: 'Light',
            value: 'light'
          }, {
            label: 'Dark',
            value: 'dark'
          }]} m={{
            b: 4
          }} />

                <TextInput name="PORTAL_BRAND_COLOR" label="Brand Color (Accent Color)" help="Primary brand color as hex code for theming the portal" placeholder="#6122E7" m={{
            b: 4
          }} />

                <StyledCard flex={{
            justify: 'flex-start',
            align: 'center',
            gap: 3
          }} p={{
            x: 4,
            y: 3
          }}>
                  <SwitchInput name="PORTAL_DISABLE_OUTPOST_BRANDING" />
                  <Div flex={{
              align: 'center',
              gap: 1
            }}>
                    <Text>Disable Outpost branding</Text>
                    <Tooltip tooltip="Help others discover Outpost by showing a small Outpost logo in the portal footer.">
                      <Icon muted icon="info" />
                    </Tooltip>
                  </Div>
                </StyledCard>

                <Button.Permission m={{
            t: 6
          }} primary submit disabled={!props_1.isValid || props_1.isSubmitting || !props_1.dirty} icon={props_1.isSubmitting ? 'loading' : 'save'}>
                  Save
                </Button.Permission>
              </Form>}
          </Formik>

          <Divider m={{
        y: 6
      }} />

          {/* Logos Form */}
          <Text bold size="l" as="h2" m={{
        t: 0,
        b: 3
      }}>
            Portal Logos
          </Text>

          <Formik enableReinitialize initialValues={logos_initial_values} validate={validateLogos} validateOnChange={false} validateOnBlur={true} onSubmit={handleLogosSubmit}>
            {props_2 => <Form>
                <TextInput name="PORTAL_LOGO" label="Logo (Light Mode)" placeholder="https://example.com/logo-light.png" m={{
            b: 4
          }} />

                <TextInput name="PORTAL_LOGO_DARK" label="Logo (Dark Mode)" placeholder="https://example.com/logo-dark.png" m={{
            b: 4
          }} />

                <TextInput name="PORTAL_FAVICON_URL" label="Favicon URL" placeholder="https://example.com/favicon.ico" m={{
            b: 0
          }} />

                <Button.Permission m={{
            t: 6
          }} primary submit disabled={!props_2.isValid || props_2.isSubmitting || !props_2.dirty} icon={props_2.isSubmitting ? 'loading' : 'save'}>
                  Save
                </Button.Permission>
              </Form>}
          </Formik>

          <Divider m={{
        y: 6
      }} />

          {/* Features */}
          <Text bold size="l" as="h2" m={{
        t: 0,
        b: 3
      }}>
            Portal Features
          </Text>

          <Formik enableReinitialize initialValues={{
        PORTAL_ENABLE_DESTINATION_FILTER: config.PORTAL_ENABLE_DESTINATION_FILTER === 'true',
        PORTAL_ENABLE_WEBHOOK_CUSTOM_HEADERS: config.PORTAL_ENABLE_WEBHOOK_CUSTOM_HEADERS === 'true'
      }} onSubmit={async (values_6, {
        resetForm: resetForm_2
      }) => {
        const updated_config_2 = {
          PORTAL_ENABLE_DESTINATION_FILTER: values_6.PORTAL_ENABLE_DESTINATION_FILTER ? 'true' : 'false',
          PORTAL_ENABLE_WEBHOOK_CUSTOM_HEADERS: values_6.PORTAL_ENABLE_WEBHOOK_CUSTOM_HEADERS ? 'true' : 'false'
        };
        const result_2 = await updateConfig(updated_config_2, {
          success: 'Portal features updated',
          failure: 'Failed to update portal features'
        });
        if (!result_2) return;
        resetForm_2({
          values: {
            PORTAL_ENABLE_DESTINATION_FILTER: result_2.PORTAL_ENABLE_DESTINATION_FILTER === 'true',
            PORTAL_ENABLE_WEBHOOK_CUSTOM_HEADERS: result_2.PORTAL_ENABLE_WEBHOOK_CUSTOM_HEADERS === 'true'
          }
        });
      }}>
            {props_3 => <Form>
                <StyledCard m={{
            b: 4
          }}>
                  <StyledCardSection flex={{
              justify: 'flex-start',
              align: 'center',
              gap: 3
            }} p={{
              x: 4,
              y: 3
            }}>
                    <SwitchInput name="PORTAL_ENABLE_DESTINATION_FILTER" />
                    <Div flex={{
                align: 'center',
                gap: 1
              }}>
                      <Text>Enable destination filter</Text>
                      <Tooltip tooltip="Allow portal users to filter events by destination">
                        <Icon muted icon="info" />
                      </Tooltip>
                    </Div>
                  </StyledCardSection>
                </StyledCard>

                <StyledCard m={{
            b: 4
          }}>
                  <StyledCardSection flex={{
              justify: 'flex-start',
              align: 'center',
              gap: 3
            }} p={{
              x: 4,
              y: 3
            }}>
                    <SwitchInput name="PORTAL_ENABLE_WEBHOOK_CUSTOM_HEADERS" />
                    <Div flex={{
                align: 'center',
                gap: 1
              }}>
                      <Text>Enable webhook custom headers</Text>
                      <Tooltip tooltip="Allow portal users to set custom headers on their webhook destinations">
                        <Icon muted icon="info" />
                      </Tooltip>
                    </Div>
                  </StyledCardSection>
                </StyledCard>

                <Button.Permission primary disabled={!props_3.dirty || props_3.isSubmitting} submit icon={props_3.isSubmitting ? 'loading' : 'save'}>
                  Save
                </Button.Permission>
              </Form>}
          </Formik>
        </>}
    </Container>;
};
export default TeamUserPortal;